import { Text, View, TouchableOpacity, Pressable, ScrollView } from 'react-native';
import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'expo-router';
import { useQuery } from '@tanstack/react-query';
import Toast from 'react-native-toast-message';
import Unicon from '@/components/General/Unicon';
import Avatar from '@/components/People/Avatar';
import { Tooltip } from '@/components/General/Tooltip';
import SelectInput from '@/components/General/inputs/SelectInput';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import ActionButton from '@/components/General/ActionButton';
import DatePickerInput from '@/components/General/inputs/DatePickerInput';
import RecurrenceSettingsNew from '@/components/Events/RecurrenceSettingsNew';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import { Query } from '@/utils/react-query/Queries';
import { supabase } from '@/data/client';
import { resolveActionValue, resolveTaskStepInfo } from './flow/actionValues';
import { AUTOMATION_DUE_TYPES } from '@/utils/constants/taskTypes';
import { isBranchSourceStep, isApprovalStep, stepBranches } from '@/utils/automationBuilder';

const STEP_TYPE_CONFIG = {
    ACTION: { icon: 'flash-outline', colorVariant: 'primary', label: 'Action' },
    CONDITION: { icon: 'git-branch-outline', colorVariant: 'warning', label: 'Condition' },
    DELAY: { icon: 'time-outline', colorVariant: 'info', label: 'Delay' },
    TASK: { icon: 'checkbox-outline', colorVariant: 'success', label: 'Task' },
};

const ACTION_TYPE_LABELS = {
    SEND_EMAIL: 'Send Email',
    SEND_SMS: 'Send SMS',
    ENROLL_TRAINING: 'Enroll in Training',
    ADD_TO_SMALL_GROUP: 'Add to Small Group',
    POST_TO_BOARD: 'Post to Board',
    ADD_TO_ROLE: 'Add to Role',
    INVITE_TO_REACH: 'Invite Person to Reach',
    TRIGGER_PATH: 'Trigger Automation',
    UPDATE_PERSON: 'Update Person',
    REGISTER_FOR_EVENT: 'Register for Event',
    CHECK_INTO_EVENT: 'Check Into Event',
    RESOLVE_PERSON: 'Resolve Person',
    UPSERT_PERSON: 'Create or Update Person',
    CREATE_TASK: 'Create Task',
    WAIT_FOR_TASK: 'Wait for Task',
    START_FOLLOW_UP_PATH: 'Start Follow Up Path',
    INVOKE_AUTOMATION: 'Invoke Automation',
    REQUEST_APPROVAL: 'Request Approval',
};

const TRIGGER_LABELS = {
    MANUAL: 'Manual Run',
    PATH_ACTION: 'Automation Action',
    PERSON_CREATED: 'Person Created',
    FORM_ENTRY: 'Form Entry',
    TRAINING_COMPLETED: 'Training Module Completed',
    SMALLGROUP_COMPLETED: 'Small Group Completed (Member)',
    SCHEDULE: 'Scheduled',
    ATTENDANCE: 'Attendance Trend',
    EVENT_CHECK_IN: 'Event Check In',
    FOLLOW_UP_ATTENTION_ENTERED: 'Attention Entered',
    FOLLOW_UP_ENROLLMENT_STARTED: 'Follow Up Enrollment Started',
    FOLLOW_UP_ENROLLMENT_COMPLETED: 'Follow Up Enrollment Completed',
    FOLLOW_UP_ENROLLMENT_CANCELLED: 'Follow Up Enrollment Cancelled',
    FOLLOW_UP_CARE_STARTED: 'Care Started',
    FOLLOW_UP_CARE_CLOSED: 'Care Closed',
    FOLLOW_UP_TOUCH_RECORDED: 'Follow Up Touch Recorded',
    TASK_COMPLETED: 'Task Completed',
    TASK_CANCELLED: 'Task Cancelled',
};

const TRIGGER_OPTIONS = Object.entries(TRIGGER_LABELS).map(([value, label]) => ({ value, label }));

const TimelineStep = ({ step, isLast, onEdit, enrollmentStepStatus, enrollmentStepDetails, enrollee, readOnly, isOutOfScope, actionLookups, taskLookups }) => {
    const { theme } = useUnistyles();
    const config = STEP_TYPE_CONFIG[step.stepType] || STEP_TYPE_CONFIG.ACTION;

    // Enrollment progress highlighting
    const stepStatus = enrollmentStepStatus;
    const isCompleted = stepStatus === 'COMPLETED' || stepStatus === 'SKIPPED';
    const isCurrent = stepStatus === 'WAITING_MANUAL' || stepStatus === 'IN_PROGRESS' || stepStatus === 'WAITING';
    const isFailed = stepStatus === 'FAILED';
    const highlightVariant = isCompleted ? 'success' : (isCurrent ? 'warning' : (isFailed ? 'error' : null));
    const actionValue = step.stepType === 'ACTION' ? resolveActionValue(step, actionLookups) : null;
    const displayName = step.name?.trim()
        || (step.stepType === 'ACTION' && step.actionType ? (ACTION_TYPE_LABELS[step.actionType] || step.actionType) : null)
        || config.label;

    const isDelayStaticPast = step.stepType === 'DELAY'
        && step.config?.delayType === 'STATIC_DATE'
        && step.config?.staticDate
        && new Date(step.config.staticDate) < new Date();
    const delayDetail = step.stepType === 'DELAY'
        ? (step.config?.delayType === 'STATIC_DATE' && step.config?.staticDate
            ? new Date(step.config.staticDate).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })
            : (step.config?.duration ? `Wait ${step.config.duration} ${step.config.unit || 'days'}` : null))
        : null;

    const taskInfo = step.stepType === 'TASK' ? resolveTaskStepInfo(step, taskLookups, enrollmentStepDetails, enrollee) : null;

    return (
        <TouchableOpacity
            style={[styles.stepRow, isOutOfScope && styles.stepRowOutOfScope]}
            activeOpacity={0.7}
            onPress={() => onEdit?.(step)}
            disabled={readOnly}
        >
            {/* Timeline line + dot */}
            <View style={styles.timelineTrack}>
                <View style={highlightVariant ? styles.timelineDot(highlightVariant) : styles.timelineDot(config.colorVariant)} >
                    <Unicon name={config.icon} size={14} colorVariant="onPrimary" />
                </View>
                {!isLast && <View style={styles.timelineLine} />}
            </View>

            {/* Step card */}
            <View style={[
                styles.stepCard,
                highlightVariant && styles.stepCardHighlighted(highlightVariant),
            ]}>
                <View style={styles.stepHeader}>
                    <View style={styles.stepTypeBadge(config.colorVariant)}>
                        <Text style={styles.stepTypeBadgeText(config.colorVariant)}>{config.label}</Text>
                    </View>
                    {step.isEnabled === false && (
                        <View style={styles.disabledBadge}>
                            <Text style={styles.disabledBadgeText}>Disabled</Text>
                        </View>
                    )}
                    {isCompleted && (
                        <View style={styles.statusPill('success')}>
                            <Text style={styles.statusPillText('success')}>Done</Text>
                        </View>
                    )}
                    {isCurrent && (
                        <View style={styles.statusPill('warning')}>
                            <Text style={styles.statusPillText('warning')}>Current</Text>
                        </View>
                    )}
                    {isFailed && (
                        <View style={styles.statusPill('error')}>
                            <Text style={styles.statusPillText('error')}>Failed</Text>
                        </View>
                    )}
                    {isDelayStaticPast && (
                        <Tooltip title="This path won't ever run past this point — the static date is in the past." appearOnTouch>
                            <Unicon name="warning-outline" size={16} colorVariant="warning" />
                        </Tooltip>
                    )}
                </View>
                <Text style={styles.stepName}>{displayName}</Text>
                {!!step.description && (
                    <Text style={styles.stepDescription} numberOfLines={2}>{step.description}</Text>
                )}
                {step.stepType === 'ACTION' && step.actionType && (
                    <Text style={styles.stepDetail}>{ACTION_TYPE_LABELS[step.actionType] || step.actionType}</Text>
                )}
                {actionValue && (
                    <Text style={styles.stepActionValue} numberOfLines={1}>{actionValue}</Text>
                )}
                {delayDetail && (
                    <Text style={[styles.stepDetail, isDelayStaticPast && { color: theme.colors.warning }]}>{delayDetail}</Text>
                )}
                {taskInfo && (
                    <>
                        <Text style={styles.stepDetail}>{taskInfo.taskName}</Text>
                        <Text style={styles.stepDetailSub}>{taskInfo.strategyLabel}</Text>
                        {taskInfo.assigneeLabel && (
                            <View style={styles.taskAssigneeRow}>
                                {taskInfo.actualAssignee && taskInfo.assigneeName && (
                                    <Avatar size={20} profile={taskInfo.actualAssignee} disableBorder />
                                )}
                                <Text
                                    style={[styles.taskAssigneeText, taskInfo.isWaitingOnTask && { color: theme.colors.warning }]}
                                    numberOfLines={1}
                                >
                                    {taskInfo.isWaitingOnTask
                                        ? (taskInfo.assigneeName
                                            ? `Waiting on task — assigned to ${taskInfo.assigneeName}`
                                            : 'Waiting on task — unassigned')
                                        : taskInfo.assigneeLabel}
                                </Text>
                            </View>
                        )}
                    </>
                )}
            </View>
        </TouchableOpacity>
    );
};

// ─── Branch tab button ───────────────────────────────────────────────────────

const BranchTab = ({ label, selected, isOutOfScope, onPress }) => (
    <TouchableOpacity
        style={[styles.branchTab(selected), isOutOfScope && styles.branchTabOutOfScope]}
        onPress={onPress}
        activeOpacity={0.7}
    >
        <Text style={[styles.branchTabText(selected), isOutOfScope && styles.branchTabTextOutOfScope]} numberOfLines={1}>{label}</Text>
    </TouchableOpacity>
);

// ─── Add Step button ─────────────────────────────────────────────────────────

const AddStepButton = ({ onAddStep, sourceStepId, branchId, branchLabel }) => (
    <View style={styles.addStepRow}>
        <View style={styles.timelineTrack}>
            <View style={styles.addStepLine} />
            <View style={styles.addStepDot}>
                <Unicon name="add" size={14} colorVariant="primary" />
            </View>
        </View>
        <Pressable
            style={styles.addStepCard}
            onPress={() => onAddStep(sourceStepId, branchId, branchLabel)}
        >
            <Text style={styles.addStepText}>Add Step</Text>
        </Pressable>
    </View>
);

// ─── Precomputed timeline tree with merge chips ─────────────────────────────-

function getBranchTargetId(conditionStepId, branchId, edgeMap) {
    const stepEdges = edgeMap[conditionStepId] || [];
    const branchEdge = stepEdges.find(e =>
        branchId === 'default'
            ? (e.branchID === 'default' || (!e.branchID && e.branchLabel === 'No Match'))
            : e.branchID === branchId
    );
    return branchEdge?.targetStepID;
}

function buildTimelineTree({ startStepId, steps, edgeMap, selectedBranchByStep, visited = new Set() }) {
    const stepList = [];
    let currentId = startStepId;
    while (currentId && !visited.has(currentId)) {
        visited.add(currentId);
        const step = steps.find(s => s.id === currentId);
        if (!step) break;
        stepList.push(step);
        if (isBranchSourceStep(step)) break;
        const stepEdges = edgeMap[currentId] || [];
        const defaultEdge = stepEdges.find(e => !e.branchID || e.branchID === 'default') || stepEdges[0];
        currentId = defaultEdge?.targetStepID;
    }

    const last = stepList[stepList.length - 1];
    let condition = null;
    let mergeInto = null;

    if (currentId && visited.has(currentId)) {
        mergeInto = steps.find(s => s.id === currentId);
    } else if (last && isBranchSourceStep(last)) {
        // CONDITION branches on config.branches (+ implicit 'default' No
        // Match); REQUEST_APPROVAL branches on fixed Approved/Denied.
        const approval = isApprovalStep(last);
        const options = [
            ...stepBranches(last).map((b, i) => ({ id: b.id, label: b.label || `Branch ${i + 1}`, isDefault: false })),
            ...(approval ? [] : [{ id: 'default', label: 'No Match', isDefault: true }]),
        ];
        const selectedBranchId = selectedBranchByStep?.[last.id] || options[0]?.id || 'default';
        condition = {
            step: last,
            branches: options.map(opt => ({
                ...opt,
                targetId: getBranchTargetId(last.id, opt.id, edgeMap),
            })),
            selectedBranchId,
            child: null,
        };
            const selectedOption = condition.branches.find(b => b.id === selectedBranchId);
            if (selectedOption?.targetId) {
                condition.child = buildTimelineTree({
                    startStepId: selectedOption.targetId,
                    steps, edgeMap, selectedBranchByStep,
                    visited: new Set(visited),
                });
            }
    }

    return { steps: stepList, condition, mergeInto };
}

function TimelineTree({ tree, onEdit, onAddStep, onSelectBranch, enrollmentStepStatus, enrollmentStepDetails, enrollee, outOfScopeBranches, readOnly, isOutOfScope, actionLookups, taskLookups, parentConditionStepId, parentBranchId, parentBranchLabel }) {
    if (!tree) return null;

    return (
        <View>
            {tree.steps.map((step, index) => {
                const isLast = index === tree.steps.length - 1 && !tree.condition && !tree.mergeInto;
                return (
                    <TimelineStep
                        key={step.id}
                        step={step}
                        isLast={isLast}
                        onEdit={onEdit}
                        enrollmentStepStatus={enrollmentStepStatus?.[step.id]}
                        enrollmentStepDetails={enrollmentStepDetails}
                        enrollee={enrollee}
                        readOnly={readOnly}
                        isOutOfScope={isOutOfScope}
                        actionLookups={actionLookups}
                        taskLookups={taskLookups}
                    />
                );
            })}

            {/* Add Step button at end of chain (if not condition/merge) */}
            {!tree.condition && !tree.mergeInto && tree.steps.length > 0 && !readOnly && onAddStep && (
                <AddStepButton
                    onAddStep={onAddStep}
                    sourceStepId={tree.steps[tree.steps.length - 1].id}
                    branchId={parentBranchId}
                    branchLabel={parentBranchLabel}
                />
            )}

            {/* Add Step button after merge chip (if merge exists) */}
            {tree.mergeInto && !readOnly && onAddStep && (
                <AddStepButton
                    onAddStep={onAddStep}
                    sourceStepId={tree.mergeInto.id}
                />
            )}

            {tree.condition && (
                <View style={styles.branchSection}>
                    <View style={styles.timelineTrack}>
                        <View style={styles.timelineLine} />
                    </View>
                    <View style={styles.branchContent}>
                        <ScrollView
                            horizontal
                            showsHorizontalScrollIndicator={false}
                            style={styles.branchTabsScroll}
                            contentContainerStyle={styles.branchTabsContent}
                        >
                            {tree.condition.branches.map((opt) => {
                                const branchOutOfScope = outOfScopeBranches?.has(`${tree.condition.step.id}:${opt.id}`);
                                return (
                                    <BranchTab
                                        key={opt.id}
                                        label={opt.label}
                                        selected={tree.condition.selectedBranchId === opt.id}
                                        isOutOfScope={branchOutOfScope}
                                        onPress={() => onSelectBranch(tree.condition.step.id, opt.id)}
                                    />
                                );
                            })}
                        </ScrollView>

                        {(() => {
                            // Approval steps have no user-defined conditions —
                            // the branch outcome is the approver's decision.
                            if (isApprovalStep(tree.condition.step)) {
                                return (
                                    <View style={styles.branchConditionSummary}>
                                        <Text style={styles.branchConditionText}>First approver to respond decides</Text>
                                    </View>
                                );
                            }
                            const selectedBranch = tree.condition.branches.find(b => b.id === tree.condition.selectedBranchId);
                            const branchData = tree.condition.step.config?.branches?.find(b => b.id === tree.condition.selectedBranchId);
                            const conditions = branchData?.conditions || [];
                            if (selectedBranch?.isDefault) {
                                return conditions.length > 0 ? (
                                    <View style={styles.branchConditionSummary}>
                                        {conditions.map((c, ci) => (
                                            <Text key={ci} style={styles.branchConditionText}>
                                                {c.key || c.ruleType} {c.operator || ''} {c.value || ''}
                                            </Text>
                                        ))}
                                    </View>
                                ) : (
                                    <View style={styles.branchConditionSummary}>
                                        <Text style={styles.branchConditionText}>No conditions — always matches</Text>
                                    </View>
                                );
                            }
                            return conditions.length > 0 ? (
                                <View style={styles.branchConditionSummary}>
                                    {conditions.map((c, ci) => (
                                        <Text key={ci} style={styles.branchConditionText}>
                                            {c.key || c.ruleType} {c.operator || ''} {c.value || ''}
                                        </Text>
                                    ))}
                                </View>
                            ) : (
                                <View style={styles.branchConditionSummary}>
                                    <Text style={styles.branchConditionText}>No conditions — always matches</Text>
                                </View>
                            );
                        })()}

                        {tree.condition.child ? (
                            <TimelineTree
                                tree={tree.condition.child}
                                onEdit={onEdit}
                                onAddStep={onAddStep}
                                onSelectBranch={onSelectBranch}
                                enrollmentStepStatus={enrollmentStepStatus}
                                enrollmentStepDetails={enrollmentStepDetails}
                                enrollee={enrollee}
                                outOfScopeBranches={outOfScopeBranches}
                                readOnly={readOnly}
                                isOutOfScope={isOutOfScope || outOfScopeBranches?.has(`${tree.condition.step.id}:${tree.condition.selectedBranchId}`)}
                                actionLookups={actionLookups}
                                taskLookups={taskLookups}
                                parentConditionStepId={parentConditionStepId || tree.condition.step.id}
                                parentBranchId={parentBranchId || tree.condition.selectedBranchId}
                                parentBranchLabel={parentBranchLabel || tree.condition.branches.find(b => b.id === tree.condition.selectedBranchId)?.label}
                            />
                        ) : (
                            !readOnly && onAddStep && (
                                <AddStepButton
                                    onAddStep={onAddStep}
                                    sourceStepId={tree.condition.step.id}
                                    branchId={tree.condition.selectedBranchId}
                                    branchLabel={tree.condition.branches.find(b => b.id === tree.condition.selectedBranchId)?.label}
                                />
                            )
                        )}
                    </View>
                </View>
            )}

            {tree.mergeInto && (
                <View style={styles.mergeRow}>
                    <View style={styles.timelineTrack}>
                        <View style={styles.timelineLine} />
                    </View>
                    <Pressable
                        style={styles.mergeChip}
                        onPress={() => onEdit?.(tree.mergeInto)}
                        disabled={readOnly}
                    >
                        <View style={styles.mergeIcon}>
                            <Unicon name="return-down-back-outline" size={14} colorVariant="primary" />
                        </View>
                        <Text style={styles.mergeText}>Continues to &ldquo;{tree.mergeInto.name}&rdquo;</Text>
                    </Pressable>
                </View>
            )}
        </View>
    );
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function PathFlowTimeline({ path: _path, steps, edges, onEditStep, onAddStep, onUpdateTrigger, onConfigureTrigger, triggerOptions, onEnrollPerson, onEnrollRole, enrolling, enrollmentStepStatus, enrollmentStepDetails, enrollee, outOfScopeBranches, readOnly, triggeringForms, triggeringEvents }) {
    const router = useRouter();
    const triggerStep = useMemo(() => steps?.find(s => s.stepType === 'TRIGGER'), [steps]);
    const pathRecurrence = _path?.Recurrence?.[0] || null;
    const [selectedBranchByStep, setSelectedBranchByStep] = useState({});
    const [scheduleRoleID, setScheduleRoleID] = useState(pathRecurrence?.roleID || null);
    const [scheduleRecurrence, setScheduleRecurrence] = useState({
        isRecurring: true,
        rruleString: pathRecurrence?.rruleString || null,
        recurrenceEndDate: pathRecurrence?.recurrenceEndDate || null,
        defaultStartTime: pathRecurrence?.firstOccurrenceAt || null,
    });

    // Lookup data for resolving action target names on step cards
    const { data: roles } = Query.roles.all({ options: { staleTime: Infinity } });
    const trainingModulesQuery = Query.trainingModules.all({ options: { staleTime: Infinity } });
    const trainingModules = trainingModulesQuery?.data?.pages?.flatMap(p => p.data) || [];
    const { data: emailTemplates } = Query.emailTemplates.all({ options: { staleTime: Infinity } });
    const { data: smallGroups } = Query.smallGroups.forDropdown({ options: { staleTime: Infinity } });
    const { data: boards } = Query.boards.all({ options: { staleTime: Infinity } });
    const { data: allPaths } = Query.paths.all({ options: { staleTime: Infinity } });
    const { data: eventInstancesForRegistration } = Query.eventInstances.forRegistration({ options: { staleTime: 1000 * 60 * 5 } });
    const actionLookups = useMemo(() => ({
        roles, trainingModules, emailTemplates, smallGroups, boards,
        paths: allPaths, eventInstances: eventInstancesForRegistration,
    }), [roles, trainingModules, emailTemplates, smallGroups, boards, allPaths, eventInstancesForRegistration]);

    // Lookup data for TASK step cards: task name resolution + per-path
    // assignment pool (for next-assignee prediction).
    const pathIDForTasks = steps?.[0]?.pathID || _path?.id || null;
    const pathsTaskQuery = Query.tasks.byDueType({ dueType: AUTOMATION_DUE_TYPES, options: { staleTime: Infinity } });
    const pathsTasks = pathsTaskQuery?.data || [];
    const { data: pathAssignmentMembers } = useQuery({
        queryKey: ['pathStepAssignmentMembers', pathIDForTasks],
        queryFn: async () => {
            const { data, error } = await supabase.from('TaskAssignmentMembers')
                .select('*, Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)')
                .eq('pathID', pathIDForTasks)
                .order('position', { ascending: true });
            if (error) return [];
            return data || [];
        },
        enabled: !!pathIDForTasks,
        staleTime: 1000 * 60 * 5,
    });
    const taskLookups = useMemo(() => {
        const tasksMap = new Map((pathsTasks || []).map(t => [t.id, t]));
        const membersByTask = new Map();
        (pathAssignmentMembers || []).forEach(m => {
            if (!membersByTask.has(m.taskID)) membersByTask.set(m.taskID, []);
            membersByTask.get(m.taskID).push(m);
        });
        return { tasks: tasksMap, membersByTask };
    }, [pathsTasks, pathAssignmentMembers]);

    useEffect(() => {
        setScheduleRoleID(pathRecurrence?.roleID || null);
        setScheduleRecurrence({
            isRecurring: true,
            rruleString: pathRecurrence?.rruleString || null,
            recurrenceEndDate: pathRecurrence?.recurrenceEndDate || null,
            defaultStartTime: pathRecurrence?.firstOccurrenceAt || null,
        });
    }, [pathRecurrence?.id]);

    const edgeMap = useMemo(() => {
        const map = {};
        (edges || []).forEach(e => {
            if (!map[e.sourceStepID]) map[e.sourceStepID] = [];
            map[e.sourceStepID].push(e);
        });
        return map;
    }, [edges]);

    const firstStepId = useMemo(() => {
        if (!triggerStep) return null;
        const triggerEdges = edgeMap[triggerStep.id] || [];
        const defaultEdge = triggerEdges.find(e => !e.branchID || e.branchID === 'default') || triggerEdges[0];
        return defaultEdge?.targetStepID;
    }, [triggerStep, edgeMap]);

    const effectiveSelected = useMemo(() => {
        if (!readOnly) return selectedBranchByStep;
        const auto = {};
        (steps || []).forEach(step => {
            if (!isBranchSourceStep(step)) return;
            const options = [
                ...stepBranches(step).map((b, i) => ({ id: b.id, label: b.label || `Branch ${i + 1}` })),
                ...(isApprovalStep(step) ? [] : [{ id: 'default', label: 'No Match' }]),
            ];
            const inScope = options.find(opt => !outOfScopeBranches?.has(`${step.id}:${opt.id}`));
            if (inScope) auto[step.id] = inScope.id;
        });
        return auto;
    }, [readOnly, steps, outOfScopeBranches, selectedBranchByStep]);

    const tree = useMemo(() =>
        firstStepId ? buildTimelineTree({ startStepId: firstStepId, steps, edgeMap, selectedBranchByStep: effectiveSelected }) : null,
    [firstStepId, steps, edgeMap, effectiveSelected]);

    const triggerConfig = triggerStep?.config || {};
    const triggerType = triggerConfig.triggerType || 'MANUAL';
    const triggerIsCompleted = readOnly && enrollmentStepStatus?.[triggerStep?.id] === 'COMPLETED';

    return (
        <View style={styles.container}>
            {/* Trigger */}
            <View style={styles.triggerRow}>
                <View style={styles.timelineTrack}>
                    <View style={triggerIsCompleted ? styles.timelineDot('success') : styles.triggerDot}>
                        <Unicon name="play-outline" size={14} colorVariant="onPrimary" />
                    </View>
                    {firstStepId && <View style={styles.timelineLine} />}
                </View>
                <View style={[styles.triggerCard, triggerIsCompleted && styles.stepCardHighlighted('success')]}>
                    <View style={styles.stepHeader}>
                        <Text style={styles.triggerLabel}>Trigger</Text>
                        {triggerIsCompleted && (
                            <View style={styles.statusPill('success')}>
                                <Text style={styles.statusPillText('success')}>Done</Text>
                            </View>
                        )}
                    </View>
                    {onUpdateTrigger ? (
                        <SelectInput
                            title="Type"
                            value={TRIGGER_LABELS[triggerType] || triggerType}
                            options={triggerOptions || TRIGGER_OPTIONS}
                            onUpdate={(value) => onUpdateTrigger(value, triggerConfig)}
                        />
                    ) : (
                        <Text style={styles.triggerName}>{TRIGGER_LABELS[triggerType] || 'Manual'}</Text>
                    )}

                    {onConfigureTrigger && (
                        <TouchableOpacity
                            onPress={onConfigureTrigger}
                            style={{ alignSelf: 'flex-start', marginTop: 6 }}
                            hitSlop={8}
                            testID="timeline-trigger-configure"
                        >
                            <Text style={styles.triggerConfigureText}>Configure trigger</Text>
                        </TouchableOpacity>
                    )}

                    {triggerType === 'MANUAL' && !readOnly && (onEnrollPerson || onEnrollRole) && (
                        <View style={styles.triggerEnrollRow}>
                            {onEnrollPerson && (
                                <SheetSelector
                                    type="people"
                                    title="Run Person"
                                    placeholder="Select a person"
                                    value={null}
                                    onChange={(p) => p?.id && onEnrollPerson(p)}
                                    disabled={enrolling}
                                >
                                    <ActionButton
                                        text="Start Run"
                                        iconName="person-add-outline"
                                        primary
                                        disabled={enrolling}
                                        testID="path-enroll-person"
                                    />
                                </SheetSelector>
                            )}
                            {onEnrollRole && (
                                <SheetSelector
                                    type="roles"
                                    title="Run Role"
                                    placeholder="Select a role"
                                    value={null}
                                    onChange={(r) => r?.id && onEnrollRole(r)}
                                    disabled={enrolling}
                                >
                                    <ActionButton
                                        text="Start Role Run"
                                        iconName="people-outline"
                                        primary
                                        disabled={enrolling}
                                        testID="path-enroll-role"
                                    />
                                </SheetSelector>
                            )}
                        </View>
                    )}

                    {triggerType === 'SCHEDULE' && !readOnly && onUpdateTrigger && (
                        <View style={{ marginTop: 8, gap: 8 }}>
                            <SheetSelector
                                type="roles"
                                title="Role for Runs"
                                placeholder="Select a role"
                                value={scheduleRoleID}
                                onChange={(r) => setScheduleRoleID(r?.id || null)}
                            >
                                <ActionButton
                                    text={scheduleRoleID ? 'Change Role' : 'Select Role'}
                                    iconName="people-outline"
                                    primary={!scheduleRoleID}
                                />
                            </SheetSelector>

                            <DatePickerInput
                                title="Start Date & Time"
                                mode="datetime"
                                value={scheduleRecurrence.defaultStartTime}
                                onUpdate={(val) => setScheduleRecurrence((prev) => ({ ...prev, defaultStartTime: val }))}
                            />

                            <RecurrenceSettingsNew
                                isShowing={true}
                                eventDetails={scheduleRecurrence}
                                setEventDetails={setScheduleRecurrence}
                            />

                            <ActionButton
                                text="Save Schedule"
                                iconName="save-outline"
                                primary
                                onPress={() => {
                                    if (!scheduleRoleID || !scheduleRecurrence.defaultStartTime || !scheduleRecurrence.rruleString) {
                                        Toast.show({ type: 'error', text2: 'Select a role, start time, and recurrence pattern.' });
                                        return;
                                    }
                                    onUpdateTrigger('SCHEDULE', {
                                        roleID: scheduleRoleID,
                                        defaultStartTime: scheduleRecurrence.defaultStartTime,
                                        rruleString: scheduleRecurrence.rruleString,
                                        recurrenceEndDate: scheduleRecurrence.recurrenceEndDate,
                                    });
                                }}
                            />
                        </View>
                    )}

                    {triggerType === 'FORM_ENTRY' && (
                        <View style={styles.triggeredBySection} testID="path-triggered-by">
                            <Text style={styles.triggeredByHeader}>Triggered by</Text>
                            <Text style={styles.triggeredByHelper}>Forms that enroll submitters into this path. Managed in each form&rsquo;s Settings → Trigger Path.</Text>
                            {(triggeringForms?.length > 0) ? (
                                <View style={styles.triggeredByList}>
                                    {triggeringForms.map((form) => (
                                        <TouchableOpacity
                                            key={form.id}
                                            style={styles.triggeredByRow}
                                            activeOpacity={0.7}
                                            onPress={() => router.push(`/forms/${form.id}`)}
                                            testID={`path-triggered-by-form-${form.id}`}
                                        >
                                            <Unicon name="document-text-outline" size={16} colorVariant="textSecondary" />
                                            <Text style={styles.triggeredByFormName} numberOfLines={1}>{form.title}</Text>
                                            {form.status !== 'published' && (
                                                <View style={styles.triggeredByStatusBadge}>
                                                    <Text style={styles.triggeredByStatusBadgeText}>{form.status}</Text>
                                                </View>
                                            )}
                                        </TouchableOpacity>
                                    ))}
                                </View>
                            ) : (
                                <Text style={styles.triggeredByEmpty}>No forms linked yet — link one from the form&rsquo;s Settings tab.</Text>
                            )}
                        </View>
                    )}

                    {triggerType === 'EVENT_CHECK_IN' && (
                        <View style={styles.triggeredBySection} testID="path-triggered-by-events">
                            <Text style={styles.triggeredByHeader}>Triggered by</Text>
                            <Text style={styles.triggeredByHelper}>Events that start runs for attendees in this automation when they check in. Managed in each event instance&rsquo;s Check-in Automation setting.</Text>
                            {(triggeringEvents?.length > 0) ? (
                                <View style={styles.triggeredByList}>
                                    {triggeringEvents.map((event) => (
                                        <TouchableOpacity
                                            key={event.id}
                                            style={styles.triggeredByRow}
                                            activeOpacity={0.7}
                                            onPress={() => router.push(`/events/${event.id}`)}
                                            testID={`path-triggered-by-event-${event.id}`}
                                        >
                                            <Unicon name="calendar-outline" size={16} colorVariant="textSecondary" />
                                            <Text style={styles.triggeredByFormName} numberOfLines={1}>{event.name}</Text>
                                        </TouchableOpacity>
                                    ))}
                                </View>
                            ) : (
                                <Text style={styles.triggeredByEmpty}>No events linked yet — link one from the event instance&rsquo;s Check-in Automation setting.</Text>
                            )}
                        </View>
                    )}
                </View>
            </View>

            {/* Flow tree from first step */}
            {tree ? (
                <TimelineTree
                    tree={tree}
                    onEdit={onEditStep}
                    onAddStep={onAddStep}
                    onSelectBranch={(stepID, branchID) => {
                        if (readOnly) return;
                        setSelectedBranchByStep(prev => ({ ...prev, [stepID]: branchID }));
                    }}
                    enrollmentStepStatus={enrollmentStepStatus}
                    enrollmentStepDetails={enrollmentStepDetails}
                    enrollee={enrollee}
                    outOfScopeBranches={outOfScopeBranches}
                    readOnly={readOnly}
                    isOutOfScope={false}
                    actionLookups={actionLookups}
                    taskLookups={taskLookups}
                />
            ) : (
                // No steps yet — show Add Step after trigger
                !readOnly && onAddStep && <AddStepButton onAddStep={onAddStep} sourceStepId={triggerStep?.id} />
            )}
        </View>
    );
}

const styles = StyleSheet.create((theme) => ({
    container: { paddingVertical: 8 },

    // Timeline shared
    timelineTrack: {
        width: 40,
        alignItems: 'center',
    },
    timelineLine: {
        flex: 1,
        width: 2,
        backgroundColor: theme.colors.border,
        minHeight: 20,
    },
    timelineDot: (colorVariant) => ({
        width: 32,
        height: 32,
        borderRadius: 16,
        backgroundColor: theme.colors[colorVariant] || theme.colors.primary,
        alignItems: 'center',
        justifyContent: 'center',
    }),

    // Trigger
    triggerRow: { flexDirection: 'row', alignItems: 'flex-start' },
    triggerDot: {
        width: 32,
        height: 32,
        borderRadius: 16,
        backgroundColor: theme.colors.primary,
        alignItems: 'center',
        justifyContent: 'center',
    },
    triggerCard: {
        flex: 1,
        marginLeft: 12,
        backgroundColor: theme.colors.primaryContainer,
        borderRadius: 10,
        padding: 12,
        marginBottom: 10,
    },
    triggerLabel: {
        fontSize: 10,
        fontFamily: theme.fonts.bold,
        color: theme.colors.primary,
        textTransform: 'uppercase',
        letterSpacing: 0.8,
        marginBottom: 2,
    },
    triggerName: {
        fontSize: 14,
        fontFamily: theme.fonts.heading,
        color: theme.colors.text,
    },
    triggerConfigureText: {
        fontSize: 12,
        fontFamily: theme.fonts.bold,
        color: theme.colors.primary,
    },
    triggerEnrollRow: {
        flexDirection: 'row',
        gap: 8,
        flexWrap: 'wrap',
        marginTop: 10,
    },

    // Triggered by (FORM_ENTRY)
    triggeredBySection: {
        marginTop: 10,
        gap: 4,
    },
    triggeredByHeader: {
        fontSize: 11,
        fontFamily: theme.fonts.bold,
        color: theme.colors.primary,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    triggeredByHelper: {
        fontSize: 11,
        color: theme.colors.textSecondary,
        lineHeight: 15,
    },
    triggeredByList: {
        marginTop: 4,
        gap: 4,
    },
    triggeredByRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        backgroundColor: theme.colors.surface,
        borderRadius: 8,
        paddingHorizontal: 8,
        paddingVertical: 6,
    },
    triggeredByFormName: {
        flex: 1,
        fontSize: 13,
        fontFamily: theme.fonts.heading,
        color: theme.colors.text,
    },
    triggeredByStatusBadge: {
        backgroundColor: theme.colors.surfaceVariant,
        borderRadius: 4,
        paddingHorizontal: 6,
        paddingVertical: 2,
    },
    triggeredByStatusBadgeText: {
        fontSize: 9,
        fontFamily: theme.fonts.bold,
        color: theme.colors.textSecondary,
        textTransform: 'uppercase',
    },
    triggeredByEmpty: {
        fontSize: 12,
        color: theme.colors.textTertiary,
        fontStyle: 'italic',
        marginTop: 4,
    },

    // Step
    stepRow: {
        flexDirection: 'row',
        alignItems: 'stretch',
    },
    stepRowOutOfScope: {
        opacity: 0.5,
    },
    stepCard: {
        flex: 1,
        marginLeft: 12,
        backgroundColor: theme.colors.surface,
        borderRadius: 10,
        padding: 12,
        marginBottom: 8,
    },
    stepHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 6,
    },
    stepTypeBadge: (colorVariant) => ({
        backgroundColor: (theme.colors[colorVariant] || theme.colors.primary) + '20',
        borderRadius: 6,
        paddingHorizontal: 8,
        paddingVertical: 2,
    }),
    stepTypeBadgeText: (colorVariant) => ({
        fontSize: 10,
        fontFamily: theme.fonts.bold,
        color: theme.colors[colorVariant] || theme.colors.primary,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    }),
    disabledBadge: {
        backgroundColor: theme.colors.surfaceVariant,
        borderRadius: 6,
        paddingHorizontal: 8,
        paddingVertical: 2,
    },
    disabledBadgeText: {
        fontSize: 10,
        fontFamily: theme.fonts.bold,
        color: theme.colors.textSecondary,
        textTransform: 'uppercase',
    },
    stepName: {
        fontSize: 15,
        fontFamily: theme.fonts.heading,
        color: theme.colors.text,
        marginBottom: 2,
    },
    stepDescription: {
        fontSize: 13,
        color: theme.colors.textSecondary,
        marginBottom: 4,
        lineHeight: 18,
    },
    stepDetail: {
        fontSize: 12,
        color: theme.colors.textTertiary,
        marginTop: 2,
    },
    stepDetailSub: {
        fontSize: 11,
        color: theme.colors.textTertiary,
        marginTop: 1,
    },
    taskAssigneeRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        marginTop: 2,
    },
    taskAssigneeText: {
        flex: 1,
        fontSize: 11,
        color: theme.colors.textTertiary,
    },
    stepActionValue: {
        fontSize: 13,
        fontFamily: theme.fonts.heading,
        color: theme.colors.text,
        marginTop: 2,
    },

    // Enrollment highlighting
    stepCardHighlighted: (colorVariant) => {
        const containerColor = theme.colors[colorVariant + 'Container'] || theme.colors.surfaceVariant;
        // Some dark-theme container colors already include an alpha channel
        // (e.g. errorContainer is #FF8A801F). Strip it before appending our own
        // alpha so we don't produce an invalid 10-digit hex that crashes native.
        const base = containerColor.length === 9 ? containerColor.slice(0, 7) : containerColor;
        return {
            borderColor: theme.colors[colorVariant] || theme.colors.primary,
            borderWidth: 2,
            backgroundColor: base + '40',
        };
    },
    statusPill: (colorVariant) => ({
        backgroundColor: (theme.colors[colorVariant] || theme.colors.primary) + '20',
        borderRadius: 6,
        paddingHorizontal: 8,
        paddingVertical: 2,
    }),
    statusPillText: (colorVariant) => ({
        fontSize: 10,
        fontFamily: theme.fonts.bold,
        color: theme.colors[colorVariant] || theme.colors.primary,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    }),

    // Branch tabs
    branchSection: {
        flexDirection: 'row',
        alignItems: 'stretch',
        marginBottom: 4,
    },
    branchContent: {
        flex: 1,
        marginLeft: 12,
    },
    branchTabsScroll: {
        flexGrow: 0,
        marginBottom: 6,
    },
    branchTabsContent: {
        gap: 6,
        paddingRight: 4,
    },
    branchTab: (selected) => ({
        paddingVertical: 5,
        paddingHorizontal: 12,
        borderRadius: 16,
        backgroundColor: selected ? theme.colors.warningContainer : theme.colors.surfaceVariant,
        borderWidth: 1,
        borderColor: selected ? theme.colors.warning : 'transparent',
    }),
    branchTabText: (selected) => ({
        fontSize: 12,
        fontFamily: theme.fonts.bold,
        color: selected ? theme.colors.warning : theme.colors.textSecondary,
    }),
    branchTabOutOfScope: {
        opacity: 0.4,
    },
    branchTabTextOutOfScope: {
        textDecorationLine: 'line-through',
    },
    branchConditionSummary: {
        backgroundColor: theme.colors.surfaceVariant,
        borderRadius: 8,
        padding: 8,
        marginBottom: 6,
    },
    branchConditionText: {
        fontSize: 11,
        color: theme.colors.textSecondary,
        marginBottom: 2,
    },

    // Merge chip
    mergeRow: {
        flexDirection: 'row',
        alignItems: 'stretch',
        marginBottom: 4,
    },
    mergeChip: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        marginLeft: 12,
        backgroundColor: theme.colors.surface,
        borderRadius: 10,
        padding: 10,
        marginBottom: 8,
        borderWidth: 1,
        borderColor: theme.colors.border,
    },
    mergeIcon: {
        marginRight: 8,
    },
    mergeText: {
        fontSize: 13,
        color: theme.colors.textSecondary,
    },

    // Add step
    addStepRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    addStepLine: {
        width: 2,
        height: 12,
        backgroundColor: theme.colors.border,
    },
    addStepDot: {
        width: 32,
        height: 32,
        borderRadius: 16,
        backgroundColor: theme.colors.primaryContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    addStepCard: {
        flex: 1,
        marginLeft: 12,
        backgroundColor: theme.colors.surface,
        borderRadius: 10,
        padding: 14,
        borderWidth: 1.5,
        borderColor: theme.colors.primary,
        borderStyle: 'dashed',
        alignItems: 'center',
        marginBottom: 10,
    },
    addStepText: {
        fontSize: 14,
        fontFamily: theme.fonts.heading,
        color: theme.colors.primary,
    },
}));
